import { useState } from 'react';
import { useTheme } from 'styled-components';

import { List, useOutsideClick } from '@genially/design-system';

import {
  StyledFilterIcon,
  StyledSuccessIcon,
  Trigger,
  TriggerLabel,
  Wrapper,
} from './QuestionUserFilter.styled';

export interface QuestionUserFilterOption {
  value: string;
  label: string;
}

interface QuestionUserFilterProps {
  options: QuestionUserFilterOption[];
  value: string;
  onChange: (value: string) => void;
}

/**
 * The filter over the participants list: the selected entry is the trigger's own label, so
 * the row reads as a sentence about what is on screen, and the choices only exist while
 * the list is open.
 */
export const QuestionUserFilter = ({
  options,
  value,
  onChange,
}: QuestionUserFilterProps) => {
  const { size } = useTheme();

  const [isOpen, setIsOpen] = useState(false);

  const close = () => {
    setIsOpen(false);
  };

  // The list renders inside this wrapper, so picking an entry counts as a click inside.
  const wrapperRef = useOutsideClick(close);

  const handleToggle = () => {
    setIsOpen(previous => !previous);
  };

  const handleSelect = (option: QuestionUserFilterOption) => () => {
    onChange(option.value);
    close();
  };

  const selectedLabel = options.find(option => option.value === value)?.label;

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger type="button" onClick={handleToggle} aria-haspopup="listbox">
        <TriggerLabel>{selectedLabel}</TriggerLabel>

        <StyledFilterIcon />
      </Trigger>

      <List
        show={isOpen}
        referenceElement={wrapperRef}
        placement={List.Placement.BOTTOM_END}
        distance={4}
        width={size.units(25)}
        variant={List.Variant.DARK}
        role="listbox"
        // Fixed rather than absolute: the panel's sections live in a scroll area, which
        // would clip a list positioned inside it.
        strategy="fixed"
      >
        {options.map(option => (
          <List.Item
            key={option.value}
            text={option.label}
            role="option"
            ariaSelected={option.value === value}
            onItemAction={handleSelect(option)}
            RightComponent={option.value === value ? <StyledSuccessIcon /> : undefined}
          />
        ))}
      </List>
    </Wrapper>
  );
};
